import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  Card,
  Row,
  Col,
  Statistic,
  Spin,
  Space,
  Typography,
  Button,
  message,
  Empty,
} from "antd";
import {
  SyncOutlined,
  InboxOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  ExclamationCircleOutlined,
  DownloadOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import Chart from "chart.js/auto";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { exportStockToExcel, exportToPDF, getFilteredRowsFromGrid } from "./exportUtils";
import AgGridWithColumnState from "../../../../components/AgGridWithColumnState";
import { getStockColumns, defaultColDef } from "./salesDashboardColumns";
import { fetchStockDataByBrandId, syncStockData } from "../../../../core/api/report";

import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";

const { Text } = Typography;

const MergedStockUpdate = ({ brandIds, brandNameMap, dateRange, refreshKey, selectedBrands, isActive }) => {
  const [loading, setLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState({});
  const [stockData, setStockData] = useState([]);
  

  const gridRef = useRef(null);
  const prevActiveRef = useRef(isActive);
  const autoSyncedRef = useRef(false);
  const brandNameMapRef = useRef(brandNameMap);
  brandNameMapRef.current = brandNameMap;
  const columnDefs = useMemo(() => getStockColumns(), []);

  useEffect(() => {
    if (!isActive) {
      autoSyncedRef.current = false;
      prevActiveRef.current = false;
      return;
    }
    if (!prevActiveRef.current) {
      const t = setTimeout(() => {
        window.dispatchEvent(new Event("resize"));
      }, 100);
      prevActiveRef.current = true;
      return () => clearTimeout(t);
    }
  }, [isActive]);

  // Auto-sync in background when Stock tab becomes active (deferred so UI + fetch paint first)
  useEffect(() => {
    if (!isActive || !brandIds.length || autoSyncedRef.current) return;
    autoSyncedRef.current = true;
    const t = setTimeout(() => {
      handleSyncAll();
    }, 150);
    return () => clearTimeout(t);
  }, [isActive, brandIds.length]);

  // Fetch stock data for all brands
  const fetchAllStockData = useCallback(async () => {
    if (!brandIds.length) {
      setStockData([]);
      return;
    }

    setLoading(true);
    try {
      const allResults = [];
      const BATCH_SIZE = 3;
      for (let i = 0; i < brandIds.length; i += BATCH_SIZE) {
        const batch = brandIds.slice(i, i + BATCH_SIZE);
        const batchResults = await Promise.all(
          batch.map((brandId) =>
            fetchStockDataByBrandId(brandId)
              .then((res) => ({
                brandId,
                brandName: brandNameMapRef.current[brandId] || `Brand ${brandId}`,
                data: res.data || res || [],
              }))
              .catch((err) => {
                console.warn(`Stock fetch failed for brand ${brandId}:`, err);
                return { brandId, brandName: brandNameMapRef.current[brandId], data: [] };
              })
          )
        );
        allResults.push(...batchResults);
      }
      const results = allResults;

      // Merge all stock data with brand column
      const SKU_NAME_SWAPS = {
        "CURCUMINOL": "CUL-30CAP-X",
        "VIBRATOR EGG": "HOM05-1-HOM-X",
      };
      const merged = [];
      results.forEach((result) => {
        const items = Array.isArray(result.data) ? result.data : [];
        items.forEach((item) => {
          let { sku, name, ...rest } = item;
          if (SKU_NAME_SWAPS[sku] === name) {
            [sku, name] = [name, sku];
          }
          merged.push({
            ...rest,
            sku,
            name,
            brand_name: result.brandName,
            _id: `stock-${result.brandId}-${item.id}`,
          });
        });
      });

      setStockData(merged);
    } catch (error) {
      console.error("Error fetching stock data:", error);
      message.error("Failed to fetch stock data");
    } finally {
      setLoading(false);
    }
  }, [brandIds]);

  useEffect(() => {
    fetchAllStockData();
  }, [fetchAllStockData, refreshKey]);

  // Summary stats
  const summary = useMemo(() => {
    const stats = {
      totalSKUs: stockData.length,
      totalOnHand: 0,
      totalAvailable: 0,
      totalBackorders: 0,
      lowStock: 0,
      outOfStock: 0,
    };
    stockData.forEach((item) => {
      stats.totalOnHand += item.on_hand || 0;
      stats.totalAvailable += item.available || 0;
      stats.totalBackorders += item.backorder_count || 0;
      if ((item.available || 0) <= 0) stats.outOfStock++;
      else if ((item.available || 0) < 1000) stats.lowStock++;
    });
    return stats;
  }, [stockData]);

  // Stock health chart
  const stockChartRef = useRef(null);
  const stockChartInstance = useRef(null);

  useEffect(() => {
    if (!stockChartRef.current || summary.totalSKUs === 0) return;
    if (stockChartInstance.current) stockChartInstance.current.destroy();

    const inStock = summary.totalSKUs - summary.lowStock - summary.outOfStock;
    stockChartInstance.current = new Chart(stockChartRef.current, {
      type: "doughnut",
      plugins: [ChartDataLabels],
      data: {
        labels: ["In Stock", "Low Stock", "Out of Stock"],
        datasets: [{
          data: [inStock, summary.lowStock, summary.outOfStock],
          backgroundColor: ["#52c41a", "#faad14", "#ff4d4f"],
        }],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          datalabels: {
            formatter: (val, ctx) => {
              const total = ctx.dataset.data.reduce((a, b) => a + b, 0);
              const pct = total > 0 ? ((val / total) * 100).toFixed(1) : 0;
              return pct > 0 ? `${pct}%` : "";
            },
            color: "#fff",
            font: { size: 12, weight: "bold" },
          },
          legend: { position: "bottom" },
        },
      },
    });

    return () => {
      if (stockChartInstance.current) stockChartInstance.current.destroy();
    };
  }, [summary]);

  const handleExportExcel = useCallback(async () => {
    const rows = getFilteredRowsFromGrid(gridRef.current, stockData);
    await exportStockToExcel(rows, columnDefs, "stock-update", gridRef.current);
    message.success("Excel exported successfully");
  }, [stockData, columnDefs]);

  const handleExportPDF = useCallback(() => {
    const rows = getFilteredRowsFromGrid(gridRef.current, stockData);
    exportToPDF(rows, columnDefs, "stock-update", "Merged Stock Update", gridRef.current);
    message.success("PDF exported successfully");
  }, [stockData, columnDefs]);

  // Export CSV
  const handleExportCSV = useCallback(() => {
    const gridApi = gridRef.current;
    if (gridApi && typeof gridApi.exportDataAsCsv === "function") {
      gridApi.exportDataAsCsv({
        fileName: `stock-update-${dayjs().format("YYYY-MM-DD")}.csv`,
      });
      message.success("CSV exported successfully");
    } else {
      message.warning("Grid not ready for export");
    }
  }, []);

  // Sync handler — skip if no credentials for this brand
  const handleSync = async (brandId) => {
    setSyncLoading((prev) => ({ ...prev, [brandId]: true }));
    try {
      await syncStockData(brandId);
      message.success(
        `ShipStation sync completed for ${brandNameMap[brandId] || "brand"}`
      );
      fetchAllStockData();
    } catch (error) {
      console.error("Sync error:", error);
      const detail = error.response?.data?.error || error.response?.data?.detail || "";
      if (detail.toLowerCase().includes("not found") || detail.toLowerCase().includes("credentials")) {
        message.warning(`ShipStation: No credentials configured for ${brandNameMap[brandId] || "this brand"}`);
      } else {
        message.error("Failed to sync ShipStation data");
      }
    } finally {
      setSyncLoading((prev) => ({ ...prev, [brandId]: false }));
    }
  };

  const handleSyncAll = async () => {
    setSyncLoading((prev) => ({ ...prev, all: true }));
    let succeeded = 0;
    let failed = 0;
    try {
      for (const id of brandIds) {
        try {
          await syncStockData(id);
          succeeded++;
        } catch {
          failed++;
        }
      }
      if (succeeded > 0) {
        message.success(`ShipStation sync completed for ${succeeded} brand${succeeded > 1 ? "s" : ""}`);
        fetchAllStockData();
      }
      if (failed > 0 && succeeded === 0) {
        message.warning("ShipStation: No credentials configured for selected brands");
      } else if (failed > 0) {
        message.info(`ShipStation: ${failed} brand${failed > 1 ? "s" : ""} skipped (no credentials)`);
      }
    } catch (error) {
      console.error("Sync error:", error);
      message.error("Failed to sync ShipStation data");
    } finally {
      setSyncLoading((prev) => ({ ...prev, all: false }));
    }
  };

  const dateRangeLabel = useMemo(() => {
    if (!dateRange || dateRange.length !== 2) return null;
    return `${dateRange[0].format("YYYY-MM-DD")} to ${dateRange[1].format("YYYY-MM-DD")}`;
  }, [dateRange]);

  if (!brandIds.length) {
    return (
      <Empty description="Select a client/brand from the global filter to view stock data" />
    );
  }

  return (
    <Spin spinning={loading}>
      {/* Date info bar */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <Space>
          <Text strong>Report Generated:</Text>
          <Text>{dayjs().format("MMM DD, YYYY HH:mm")}</Text>
          {dateRangeLabel && (
            <>
              <Text type="secondary">|</Text>
              <Text strong>Period:</Text>
              <Text>{dateRangeLabel}</Text>
            </>
          )}
        </Space>
      </div>

      {/* Summary Statistics */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col span={4}>
            <Statistic
              title="Total SKUs"
              value={summary.totalSKUs}
              prefix={<InboxOutlined />}
              valueStyle={{ color: "#1890ff" }}
            />
          </Col>
          <Col span={4}>
            <Statistic
              title="Total On Hand"
              value={summary.totalOnHand}
              valueStyle={{ color: "#52c41a" }}
            />
          </Col>
          <Col span={4}>
            <Statistic
              title="Total Available"
              value={summary.totalAvailable}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: "#52c41a" }}
            />
          </Col>
          <Col span={4}>
            <Statistic
              title="Total Back Orders"
              value={summary.totalBackorders}
              prefix={<ExclamationCircleOutlined />}
              valueStyle={{ color: summary.totalBackorders > 0 ? "#ff4d4f" : "#8c8c8c" }}
            />
          </Col>
          <Col span={4}>
            <Statistic
              title="Low Stock (< 1000)"
              value={summary.lowStock}
              prefix={<WarningOutlined />}
              valueStyle={{ color: "#faad14" }}
            />
          </Col>
          <Col span={4}>
            <Statistic
              title="Out of Stock"
              value={summary.outOfStock}
              valueStyle={{ color: "#ff4d4f" }}
            />
          </Col>
        </Row>
      </Card>

      {/* Stock Health Chart */}
      {summary.totalSKUs > 0 && (
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={8}>
            <Card title="Stock Health Overview" size="small">
              <div style={{ height: 220 }}>
                <canvas ref={stockChartRef} />
              </div>
            </Card>
          </Col>
        </Row>
      )}

      {/* Sync Controls + Grid — low stock &lt; 1000 */}
      <Card
        title={
          <span>
            Inventory Data — All Clients
            <Text type="secondary" style={{ fontSize: 12, fontWeight: 400, marginLeft: 8 }}>
              Low stock: &lt; 1000
            </Text>
          </span>
        }
        size="small"
        extra={
          <Space size="small">
            <Button
              type="primary"
              size="small"
              icon={<SyncOutlined spin={syncLoading.all} />}
              onClick={handleSyncAll}
              loading={syncLoading.all}
            >
              Sync ShipStation
            </Button>
            <Button size="small" icon={<DownloadOutlined />} onClick={handleExportCSV}>
              CSV
            </Button>
            <Button
              size="small"
              icon={<FileExcelOutlined />}
              onClick={handleExportExcel}
              style={{ color: "#52c41a", borderColor: "#52c41a" }}
            >
              Excel
            </Button>
            <Button
              size="small"
              icon={<FilePdfOutlined />}
              onClick={handleExportPDF}
              style={{ color: "#ff4d4f", borderColor: "#ff4d4f" }}
            >
              PDF
            </Button>
          </Space>
        }
      >
        <div
          className="ag-theme-alpine"
          style={{ height: "calc(100vh - 200px)", minHeight: 500, width: "100%" }}
        >
          <AgGridWithColumnState
            tableId="sales-dashboard-stock-update"
            gridApiRef={gridRef}
            rowData={stockData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            pagination={true}
            paginationPageSize={100}
            paginationPageSizeSelector={[50, 100, 200, 500]}
            cellSelection={true}
            enableCellTextSelection={true}
            suppressMovableColumns={false}
            animateRows={false}
            getRowId={(params) => params.data._id}
            sideBar={{
              toolPanels: [
                {
                  id: "columns",
                  labelDefault: "Columns",
                  labelKey: "columns",
                  iconKey: "columns",
                  toolPanel: "agColumnsToolPanel",
                },
                {
                  id: "filters",
                  labelDefault: "Filters",
                  labelKey: "filters",
                  iconKey: "filter",
                  toolPanel: "agFiltersToolPanel",
                },
              ],
            }}
          />
        </div>
      </Card>
    </Spin>
  );
};

export default MergedStockUpdate;
